/* p483-zoom: the 483rd project page's maps, opened from their pictures to pan around and zoom into,
   down to the poster at 300 dpi (sections/kr-project-483rd.liquid).

   The maps are Deep Zoom tiles on Cloudflare R2 behind 483rd.krmaps.com/media (made and uploaded by
   docs/projects/483rd/publish_zoom.py; where they are: snippets/p483-zoom-data.liquid), drawn by
   OpenSeadragon (assets/p483-openseadragon.min.js), which loads only when a map is first opened.

   Back closes the viewer, as on the Press pages: opening pushes a history entry at the SAME url,
   popstate closes, and a close by hand (Close, Esc) spends that entry with history.back(); `owed`
   counts those backs, so a late popstate never shuts a viewer opened again straight after.

   A link can open a map straight away, to send to someone (Kevin, 7 Oct 2026):
   /pages/483rd-bomb-group#his-map-simplified (and the others in LINKED). The hash is dropped
   before the viewer opens, so Close or Back leaves them on the page itself.

   A map drawn two ways (PAIRS: simplified, after Minard, and as recorded) gets a switch in the bar
   that swaps the one for the other in place, on the same spot at the same zoom (both are drawn on
   the same projection and frame), without a new history entry. One link opens either with the
   switch: #his-map (Kevin, 7 Oct 2026). */
(() => {
  const me = document.currentScript;
  const dataEl = document.getElementById('p483-zoom-data');
  const dlg = document.getElementById('p483-zoom');
  if (!me || !dataEl || !dlg) return;
  const maps = JSON.parse(dataEl.textContent);
  const title = dlg.querySelector('[data-zoom-heading]');
  const holder = dlg.querySelector('[data-zoom-map]');
  const pairEl = dlg.querySelector('[data-zoom-pair]');
  let viewer = null, shown = null, owed = 0, osd = null, drawn = false;

  const PAIRS = { his: { simplified: 'his', recorded: 'hisroutes' }, group: { simplified: 'group', recorded: 'courses' } };
  const pairOf = key => Object.values(PAIRS).find(p => p.simplified === key || p.recorded === key);
  const titles = {};   // each map's title, from the first link to it on the page
  document.querySelectorAll('[data-p483-zoom]').forEach(a => { titles[a.dataset.p483Zoom] = titles[a.dataset.p483Zoom] || a.dataset.zoomTitle || ''; });
  const showPair = key => {
    const p = pairOf(key);
    const both = p && maps[p.simplified] && maps[p.recorded];
    pairEl.hidden = !both;
    if (both) pairEl.querySelectorAll('[data-zoom-ver]').forEach(b => b.setAttribute('aria-pressed', String(p[b.dataset.zoomVer] === key)));
  };

  const loadOsd = () => osd || (osd = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = me.dataset.osd;
    s.onload = resolve;
    s.onerror = () => { osd = null; reject(new Error('the map viewer did not load')); };
    document.head.appendChild(s);
  }));

  const source = d => ({
    Image: {
      xmlns: 'http://schemas.microsoft.com/deepzoom/2008',
      Url: d.url, Format: d.format, Overlap: String(d.overlap), TileSize: String(d.tileSize),
      Size: { Width: String(d.width), Height: String(d.height) },
    },
  });

  const make = () => {
    const wide = window.matchMedia('(min-width: 700px)').matches;
    viewer = window.OpenSeadragon({
      element: holder,
      drawer: 'canvas',
      crossOriginPolicy: 'Anonymous',
      showNavigationControl: false,           // the bar's own buttons do this
      showNavigator: wide,                    // the whole map in the corner, with where you are on it (once zoomed in)
      navigatorPosition: 'BOTTOM_RIGHT',
      navigatorSizeRatio: 0.16,
      navigatorAutoFade: false,
      navigatorBackground: '#e9e1cc',
      navigatorBorderColor: '#5b5043',
      navigatorDisplayRegionColor: '#8f4b2c',
      navigatorOpacity: 1,
      visibilityRatio: 1,                     // the map never slides off the screen
      constrainDuringPan: true,
      minZoomImageRatio: 1,
      maxZoomPixelRatio: 1.25,                // a little past one tile pixel to one screen pixel
      animationTime: 0.6,
      zoomPerScroll: 1.3,
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true, flickEnabled: true },
      gestureSettingsTouch: { pinchRotate: false },
      immediateRender: false,
      blendTime: 0.15,
    });
    viewer.addHandler('open-failed', fail);
    // nothing drawn and a tile has failed: the tiles cannot be reached, so say so instead of a blank screen
    viewer.addHandler('tile-loaded', () => { drawn = true; });
    viewer.addHandler('tile-load-failed', () => { if (!drawn) fail(); });
    if (viewer.navigator) {   // at the whole map the corner map only covers the legend
      const nav = viewer.navigator.element;
      const sync = () => {
        const vp = viewer.viewport;
        nav.style.visibility = vp.getZoom() > vp.getHomeZoom() * 1.15 ? 'visible' : 'hidden';
      };
      viewer.addHandler('open', sync);
      viewer.addHandler('zoom', sync);
    }
  };

  const fail = () => {
    holder.setAttribute('data-failed', '');
  };

  const open = async link => {
    const key = link.dataset.p483Zoom;
    const d = maps[key];
    if (!d) return false;
    title.textContent = link.dataset.zoomTitle || titles[key] || '';
    showPair(key);
    holder.removeAttribute('data-failed');
    document.documentElement.classList.add('krpj-zoom-open');
    dlg.showModal();
    history.pushState({ p483Zoom: key }, '', location.href);
    try {
      await loadOsd();
    } catch (e) {
      fail();
      return true;
    }
    if (!dlg.open) return true;
    if (!viewer) make();
    if (shown !== key) {
      drawn = false;
      viewer.open(source(d));
      shown = key;
    } else {
      viewer.viewport.goHome(true);
    }
    viewer.canvas.focus({ preventScroll: true });   // arrows and + / - work at once
    return true;
  };

  const teardown = () => {
    document.documentElement.classList.remove('krpj-zoom-open');
    if (dlg.open) dlg.close();
  };

  // a close by hand: shut it, then spend the history entry it pushed
  const closeByHand = () => {
    if (!dlg.open) return;
    teardown();
    if (history.state && history.state.p483Zoom) { owed++; history.back(); }
  };

  window.addEventListener('popstate', () => {
    if (owed) { owed--; return; }
    teardown();
  });

  dlg.addEventListener('cancel', e => { e.preventDefault(); closeByHand(); });   // Esc
  // the other version of the same map, on the same spot at the same zoom
  const swapTo = key => {
    if (!maps[key] || key === shown || !viewer || !viewer.world.getItemCount()) return;
    const at = viewer.viewport.getBounds(true);
    title.textContent = titles[key] || '';
    showPair(key);
    holder.removeAttribute('data-failed');
    drawn = false;
    viewer.addOnceHandler('open', () => viewer.viewport.fitBounds(at, true));
    viewer.open(source(maps[key]));
    shown = key;
  };

  dlg.addEventListener('click', e => {
    const v = e.target.closest('[data-zoom-ver]');
    if (v) {
      const p = pairOf(shown);
      if (p) swapTo(p[v.dataset.zoomVer]);
      return;
    }
    const b = e.target.closest('[data-zoom]');
    if (!b) return;
    const act = b.dataset.zoom;
    if (act === 'close') return closeByHand();
    if (!viewer || !viewer.world.getItemCount()) return;
    const vp = viewer.viewport;
    if (act === 'in') vp.zoomBy(1.6);
    else if (act === 'out') vp.zoomBy(1 / 1.6);
    else if (act === 'home') vp.goHome();
    vp.applyConstraints();
  });

  const LINKED = { '#his-map': 'his', '#group-map': 'group', '#his-map-simplified': 'his', '#his-map-as-recorded': 'hisroutes', '#map-simplified': 'group', '#map-as-recorded': 'courses' };
  const linked = LINKED[location.hash.toLowerCase()];
  const first = linked && document.querySelector(`[data-p483-zoom="${linked}"]`);
  if (first && maps[linked]) {
    history.replaceState(history.state, '', location.pathname + location.search);
    open(first);
  }

  document.addEventListener('click', e => {
    const link = e.target.closest('[data-p483-zoom]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!maps[link.dataset.p483Zoom]) return;   // no tiles for it: the link opens the picture instead
    e.preventDefault();
    open(link);
  });
})();
